import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Camera,
  Download,
  Mic,
  Radio,
  RotateCcw,
  ShieldCheck,
  SlidersHorizontal,
  Timer,
  Trash2,
  Video,
} from 'lucide-react';
import api from '../../services/api';
import EmployerPageShell from '../../components/employer/EmployerPageShell';
import {
  calculateSpikeLatencySummary,
  transitionLiveAvatarSpikeState,
  type LiveAvatarSpikeState,
  type SpikeLatencyInput,
} from './liveAvatarSpikeState';

type ProviderStatus =
  | 'NOT_CONFIGURED'
  | 'READY'
  | 'RUNNING'
  | 'PASSED'
  | 'FAILED'
  | 'BLOCKED'
  | 'TECHNICALLY_DISQUALIFIED';

type SourceStatus = 'VERIFIED_PUBLIC_FACT' | 'PROVIDER_CLAIM' | 'NOT_PUBLICLY_DOCUMENTED' | 'MUST_PROVE_IN_SPIKE';

type PricingMetadata = {
  publicChargingUnit: string;
  publicPlanName: string;
  publicSessionDurationLimit: string;
  publicConcurrencyLimit: string;
  pricingPubliclyAvailable: boolean;
  pricingNote: string;
};

type ProviderDefinition = {
  id: string;
  providerName: string;
  productMode: string;
  architectureCategory: 'MANAGED_CONVERSATION' | 'MODULAR_AVATAR_RENDERING';
  optInFlagName: string;
  requiredCredentialNames: string[];
  optionalCredentialNames: string[];
  liveKitRole: Record<string, string | boolean>;
  recordingArchitecture: {
    roomOwner: string;
    recordingController: string;
    candidateTrackRecordingAvailable: 'PROVEN' | 'UNPROVEN' | 'NOT_AVAILABLE';
    avatarTrackRecordingAvailable: 'PROVEN' | 'UNPROVEN' | 'NOT_AVAILABLE';
    combinedRoomRecordingAvailable: 'PROVEN' | 'UNPROVEN' | 'NOT_AVAILABLE';
    separateTrackExportAvailable: 'PROVEN' | 'UNPROVEN' | 'NOT_AVAILABLE';
    recordingAssetOwnership: string;
    recordingRetrievalMechanism: string;
    deletionMechanism: string;
    transcriptToRecordingTimestampAlignment: 'PROVEN' | 'UNPROVEN' | 'NOT_AVAILABLE';
    intellerviewCanInitiateAndStopRecording: boolean;
    continuesIfCandidateRefreshes: 'YES' | 'NO' | 'UNPROVEN';
    productionSuitability:
      | 'CENTRAL_RECORDING_PROVEN'
      | 'UNPROVEN_PROVIDER_CONTROLLED_RECORDING'
      | 'BLOCKED_UNTIL_CENTRAL_RECORDING_PROVEN';
    note: string;
  };
  readinessNotes: string[];
  sessionDurationLimit: string;
  concurrency: string;
  pricing: PricingMetadata;
  trialAllowance: string;
  setupSteps: string[];
  capabilities: Array<{ label: string; status: SourceStatus; note: string }>;
  blockers: string[];
  warnings: string[];
  readiness: {
    status: ProviderStatus;
    optInFlagName: string;
    optInEnabled: boolean;
    canStartRealSession: boolean;
    configuredCredentialNames: string[];
    missingCredentialNames: string[];
    blockedReason: string;
  };
};

type SpikeResult = {
  providerId: string;
  providerName: string;
  productMode: string;
  providerConversationId: string | null;
  status: ProviderStatus;
  avatarRenderResult: string;
  candidateCameraResult: string;
  exactQuestionResult: string;
  followUpResult: string;
  closingResult: string;
  transcriptResult: string;
  timestampResult: string;
  recordingResult: string;
  candidateTrackResult: string;
  reconnectResult: string;
  cleanupResult: string;
  shortLivedCredentialReturned: boolean;
  browserSession: {
    embedStrategy: 'tavus_iframe' | 'liveavatar_livekit_meet';
    providerConversationId: string;
    conversationUrl: string;
    joinUrl: string;
    hasShortLivedCredential: boolean;
  } | null;
  transcriptStatus: {
    eventSeen: boolean;
    available: boolean;
    segmentCount: number;
    speakerLabelsAvailable: boolean;
    timestampsAvailable: boolean;
    rawTranscriptReturned: false;
    note: string;
  } | null;
  recordingStatus: {
    eventSeen: boolean;
    available: boolean;
    storageProvider: string | null;
    storageUriAvailable: boolean;
    frontendStartRequired: boolean;
    storageConfigurationRequired: boolean;
    candidateOnlyAvailable: boolean;
    note: string;
  } | null;
  transcriptEvidence: {
    source: 'callback' | 'verbose_status' | 'worker_event' | 'none';
    coreQuestionDetected: boolean;
    coreQuestionCount: number;
    followUpDetected: boolean;
    followUpCount: number;
    maxOneFollowUp: boolean;
    closingDetected: boolean;
    assistantTurnCount: number;
    candidateTurnCount: number;
    transcriptTimestampAvailable: boolean;
    transcriptSpeakerLabelsAvailable: boolean;
    rawTranscriptReturned: false;
    note: string;
  } | null;
  workerEventSummary?: {
    roomName: string;
    eventCount: number;
    completionEventSeen: boolean;
    failedEventSeen: boolean;
    rawTranscriptReturned: false;
  } | null;
  workerEvents?: Array<{
    type: string;
    state: string;
    relativeTimestampMs: number;
    speaker: 'avatar' | 'candidate' | null;
    final: boolean | null;
    confidenceBand: string | null;
    rawTranscriptReturned: false;
  }>;
  transcriptTiming?: {
    available: boolean;
    segmentCount: number;
    candidateSegmentCount: number;
    assistantSegmentCount: number;
    firstSegmentStartMs: number | null;
    lastSegmentEndMs: number | null;
    firstCandidateSegmentStartMs: number | null;
    lastCandidateSegmentEndMs: number | null;
    confidence: 'HIGH' | 'MEDIUM' | 'LOW';
    warnings: string[];
    rawTranscriptReturned: false;
    note: string;
  } | null;
  providerEventNames?: string[];
  latencyMeasurements: Record<string, number | null>;
  blockers: string[];
  warnings: string[];
  normalizedError: { code: string; message: string; retryable: boolean } | null;
};

type LiveKitEgressResult = {
  egressId: string | null;
  roomName: string;
  participantIdentity: string;
  objectKey: string;
  started: boolean;
  active: boolean;
  stopped: boolean;
  completed: boolean;
  duplicatePrevented: boolean;
  stopReason: 'manual' | 'timeout' | 'cleanup' | 'auto_complete' | 'not_stopped';
  status: string;
  startedAt: string | null;
  stoppedAt: string | null;
  durationMs: number | null;
  fileSizeBytes: number | null;
  storageProvider: 'Cloudflare R2';
  publicUrlCreated: false;
  normalizedError: { code: string; message: string; retryable: boolean } | null;
};

type SpikeConfig = {
  plan: {
    banner: string;
    coreQuestion: string;
    fictionalAnswerWithoutOutcome: string;
    fictionalFollowUpAnswer: string;
    allowedFollowUpPurpose: string;
    controlledFollowUp: string;
    maxFollowUps: number;
    stopCondition: string;
    disallowedTopics: string[];
  };
  selectedProviderPath: string;
  standardTestScript: string[];
  providers: ProviderDefinition[];
};

type TranscriptSegment = {
  speaker: 'avatar' | 'candidate';
  text: string;
  isFinal: boolean;
  startedAtMs: number | null;
  endedAtMs: number | null;
  confidence: number | null;
  association: 'introduction' | 'core_question' | 'follow_up' | 'closing';
};

const stateDescriptions: Record<LiveAvatarSpikeState, string> = {
  IDLE: 'Waiting for local camera and microphone check.',
  REQUESTING_MEDIA: 'Requesting camera and microphone permission.',
  READY: 'Local media is ready. A selected provider boundary can be requested.',
  CONNECTING: 'Requesting the backend provider boundary.',
  CONNECTED: 'Provider boundary returned a successful result.',
  AVATAR_SPEAKING: 'Avatar is speaking.',
  CANDIDATE_SPEAKING: 'Candidate is speaking.',
  PROCESSING: 'Processing candidate answer.',
  FOLLOW_UP: 'One controlled follow-up is available.',
  CLOSING: 'Closing the spike interview.',
  COMPLETED: 'Spike completed.',
  RECONNECTING: 'Attempting reconnect.',
  FAILED: 'Spike is blocked or failed. Review the provider result and retry safely.',
};

const ratingFields = [
  'realism',
  'lip sync',
  'natural listening',
  'natural speaking',
  'facial movement',
  'gaze',
  'gesture quality',
  'voice naturalness',
  'conversational flow',
  'perceived latency',
  'professionalism',
  'mobile visual quality',
  'overall candidate experience',
];

const sourceClass: Record<SourceStatus, string> = {
  VERIFIED_PUBLIC_FACT: 'border-[#0f766e] bg-[#ccfbf1] text-[#115e59]',
  PROVIDER_CLAIM: 'border-[#0284c7] bg-[#f0f9ff] text-[#075985]',
  NOT_PUBLICLY_DOCUMENTED: 'border-[#d97706] bg-[#fffbeb] text-[#92400e]',
  MUST_PROVE_IN_SPIKE: 'border-[#64748b] bg-[#f8fafc] text-[#475569]',
};

const statusClass: Record<ProviderStatus, string> = {
  NOT_CONFIGURED: 'border-[#64748b] bg-[#f8fafc] text-[#475569]',
  READY: 'border-[#16a34a] bg-[#f0fdf4] text-[#166534]',
  RUNNING: 'border-[#0284c7] bg-[#f0f9ff] text-[#075985]',
  PASSED: 'border-[#0f766e] bg-[#ccfbf1] text-[#115e59]',
  FAILED: 'border-[#dc2626] bg-[#fef2f2] text-[#991b1b]',
  BLOCKED: 'border-[#d97706] bg-[#fffbeb] text-[#92400e]',
  TECHNICALLY_DISQUALIFIED: 'border-[#dc2626] bg-[#fef2f2] text-[#991b1b]',
};

const formatLatency = (value: number | null) => (typeof value === 'number' ? `${value} ms` : 'Not measured');

const formatCapabilityValue = (value: string | boolean) => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return value.replaceAll('_', ' ');
};

const LIVEAVATAR_STATUS_POLL_MS = 5000;
const LIVEAVATAR_COMPLETION_SETTLE_MS = 8000;
const LIVEAVATAR_HARD_TIMEOUT_MS = 118000;

const hasCompletedInterviewEvidence = (evidence: SpikeResult['transcriptEvidence'] | null | undefined) =>
  Boolean(
    evidence &&
      evidence.coreQuestionDetected &&
      evidence.coreQuestionCount === 1 &&
      evidence.followUpCount <= 1 &&
      evidence.maxOneFollowUp &&
      evidence.closingDetected &&
      evidence.candidateTurnCount >= 1 &&
      evidence.assistantTurnCount >= 2 &&
      evidence.transcriptTimestampAvailable &&
      evidence.transcriptSpeakerLabelsAvailable &&
      !evidence.rawTranscriptReturned
  );

const buildCompletionEvidenceSignature = (statusResult: any): string | null => {
  const evidence = statusResult?.transcriptEvidence;
  if (!hasCompletedInterviewEvidence(evidence)) return null;
  return [
    statusResult?.transcriptStatus?.segmentCount ?? 'unknown',
    evidence.coreQuestionCount,
    evidence.followUpCount,
    evidence.assistantTurnCount,
    evidence.candidateTurnCount,
    evidence.closingDetected ? 'closed' : 'not_closed',
  ].join(':');
};

const LiveAvatarSpike: React.FC = () => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [config, setConfig] = useState<SpikeConfig | null>(null);
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [state, setState] = useState<LiveAvatarSpikeState>('IDLE');
  const [error, setError] = useState<string | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [microphoneReady, setMicrophoneReady] = useState(false);
  const [videoTrackActive, setVideoTrackActive] = useState(false);
  const [audioTrackActive, setAudioTrackActive] = useState(false);
  const [trackWarning, setTrackWarning] = useState<string | null>(null);
  const [latencyInput, setLatencyInput] = useState<SpikeLatencyInput>({});
  const [transcriptSegments] = useState<TranscriptSegment[]>([]);
  const [providerResult, setProviderResult] = useState<SpikeResult | null>(null);
  const [cleanupMessage, setCleanupMessage] = useState<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [humanRatings, setHumanRatings] = useState<Record<string, string>>({});
  const [humanNotes, setHumanNotes] = useState('');
  const [providerStatusRefreshed, setProviderStatusRefreshed] = useState(false);
  const [providerSessionStopped, setProviderSessionStopped] = useState(false);
  const [autoCompletionMessage, setAutoCompletionMessage] = useState<string | null>(null);
  const [egressResult, setEgressResult] = useState<LiveKitEgressResult | null>(null);
  const [egressMessage, setEgressMessage] = useState<string | null>(null);
  const [signedPlaybackUrl, setSignedPlaybackUrl] = useState<string | null>(null);
  const [signedPlaybackExpiresAt, setSignedPlaybackExpiresAt] = useState<string | null>(null);
  const autoCompletionInProgressRef = useRef(false);
  const completionCandidateRef = useRef<{ signature: string; firstSeenAtMs: number } | null>(null);

  const latencySummary = useMemo(() => calculateSpikeLatencySummary(latencyInput), [latencyInput]);
  const selectedProvider = useMemo(
    () => config?.providers.find((provider) => provider.id === selectedProviderId) || config?.providers[0] || null,
    [config?.providers, selectedProviderId]
  );

  const applyEvent = (event: Parameters<typeof transitionLiveAvatarSpikeState>[1]) => {
    setState((current) => transitionLiveAvatarSpikeState(current, event));
  };

  const getProviderConversationId = useCallback(
    () => providerResult?.providerConversationId || providerResult?.browserSession?.providerConversationId || null,
    [providerResult?.browserSession?.providerConversationId, providerResult?.providerConversationId]
  );

  const mergeProviderStatusResult = useCallback((statusResult: any) => {
    setProviderResult((current) =>
      current
        ? {
            ...current,
            transcriptStatus: statusResult?.transcriptStatus || current.transcriptStatus,
            recordingStatus: statusResult?.recordingStatus || current.recordingStatus,
            transcriptEvidence: statusResult?.transcriptEvidence || current.transcriptEvidence,
            transcriptTiming: statusResult?.transcriptTiming || current.transcriptTiming,
            workerEventSummary: statusResult?.workerEventSummary || current.workerEventSummary,
            workerEvents: statusResult?.workerEvents || current.workerEvents,
            providerEventNames: statusResult?.providerEventNames || current.providerEventNames,
            transcriptResult: statusResult?.transcriptStatus?.note || current.transcriptResult,
            recordingResult: statusResult?.recordingStatus?.note || current.recordingResult,
            candidateTrackResult: statusResult?.candidateTrackResult || current.candidateTrackResult,
            reconnectResult: statusResult?.reconnectResult || current.reconnectResult,
            cleanupResult: statusResult?.cleanupResult || current.cleanupResult,
            latencyMeasurements: {
              ...current.latencyMeasurements,
              ...(statusResult?.latencyMeasurements || {}),
            },
          }
        : current
    );
  }, []);


  useEffect(() => {
    let mounted = true;
    api
      .get('/ai-interviews/spike/live-avatar/config')
      .then((response) => {
        if (!mounted) return;
        setConfig(response.data);
        setSelectedProviderId(response.data?.providers?.[0]?.id || '');
      })
      .catch((configError: any) => {
        if (!mounted) return;
        const backendMessage = configError.response?.data?.error || configError.message;
        setError(backendMessage || 'Unable to load live-avatar spike configuration.');
        setState('FAILED');
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    return () => {
      localStream?.getTracks().forEach((track) => track.stop());
    };
  }, [localStream]);

  const requestMedia = async () => {
    setError(null);
    setTrackWarning(null);
    setState('REQUESTING_MEDIA');

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('This browser does not expose camera and microphone permissions.');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setLocalStream(stream);

      const videoTrack = stream.getVideoTracks()[0] || null;
      const audioTrack = stream.getAudioTracks()[0] || null;
      setCameraReady(Boolean(videoTrack));
      setMicrophoneReady(Boolean(audioTrack));
      setVideoTrackActive(Boolean(videoTrack && videoTrack.readyState === 'live' && videoTrack.enabled));
      setAudioTrackActive(Boolean(audioTrack && audioTrack.readyState === 'live' && audioTrack.enabled));

      videoTrack?.addEventListener('ended', () => {
        setVideoTrackActive(false);
        setTrackWarning('Local video track ended. A production interview would record this as a technical warning.');
      });
      audioTrack?.addEventListener('ended', () => {
        setAudioTrackActive(false);
        setTrackWarning('Local audio track ended. A production interview would pause or route to technical review.');
      });
      videoTrack?.addEventListener('mute', () => {
        setVideoTrackActive(false);
        setTrackWarning('Local video track muted. This is an integrity/technical warning only.');
      });
      audioTrack?.addEventListener('mute', () => {
        setAudioTrackActive(false);
        setTrackWarning('Local audio track muted. This is an integrity/technical warning only.');
      });
      videoTrack?.addEventListener('unmute', () => setVideoTrackActive(true));
      audioTrack?.addEventListener('unmute', () => setAudioTrackActive(true));

      setState('READY');
    } catch (mediaError: any) {
      setError(mediaError?.message || 'Camera and microphone permission is required for this spike.');
      setState('FAILED');
    }
  };

  const releaseLocalPreviewForProviderRoom = () => {
    localStream?.getTracks().forEach((track) => track.stop());
    setLocalStream(null);
    setVideoTrackActive(false);
    setAudioTrackActive(false);
    setTrackWarning(
      'Local preview released before the LiveKit room joins. The official candidate camera/microphone feed is published inside the provider room.'
    );
  };

  const connectProvider = async () => {
    if (!selectedProvider) return;
    setError(null);
    setProviderResult(null);
    setCleanupMessage(null);
    setProviderStatusRefreshed(false);
    setProviderSessionStopped(false);
    setEgressResult(null);
    setEgressMessage(null);
    setSignedPlaybackUrl(null);
    setSignedPlaybackExpiresAt(null);
    completionCandidateRef.current = null;
    setAutoCompletionMessage(null);
    setIframeLoaded(false);
    setState('CONNECTING');
    const connectRequestedAtMs = performance.now();
    setLatencyInput((current) => ({ ...current, connectRequestedAtMs }));

    try {
      const response = await api.post('/ai-interviews/spike/live-avatar/session', {
        providerId: selectedProvider.id,
        browserDevice: navigator.userAgent,
      });
      const result = response.data?.result || response.data;
      if (selectedProvider.id === 'liveavatar_lite') {
        releaseLocalPreviewForProviderRoom();
      }
      setProviderResult(result);
      setLatencyInput((current) => ({
        ...current,
        connectedAtMs: performance.now(),
        firstAvatarRenderAtMs: result?.latencyMeasurements?.firstAvatarFrameMs || null,
      }));
      setState(result?.status === 'PASSED' || result?.status === 'RUNNING' ? 'CONNECTED' : 'FAILED');
      if (result?.normalizedError?.message) setError(result.normalizedError.message);
    } catch (connectError: any) {
      const backendMessage = connectError.response?.data?.error || connectError.message;
      const missingCredentials = connectError.response?.data?.missingCredentialNames;
      const result = connectError.response?.data?.result;
      if (result) setProviderResult(result);
      setError(
        Array.isArray(missingCredentials) && missingCredentials.length > 0
          ? `${backendMessage} Missing: ${missingCredentials.join(', ')}.`
          : backendMessage || 'Live avatar provider connection failed.'
      );
      setState('FAILED');
    }
  };

  const cleanupProvider = async () => {
    if (!selectedProvider) return;
    setError(null);
    const providerConversationId = getProviderConversationId();
    if (selectedProvider.id === 'liveavatar_full' && providerConversationId && (!providerSessionStopped || !providerStatusRefreshed)) {
      setError('End the LiveAvatar session and refresh provider status before cleanup. This preserves transcript evidence first.');
      return;
    }
    try {
      const response = providerConversationId
        ? await api.post(`/ai-interviews/spike/live-avatar/session/${encodeURIComponent(providerConversationId)}/cleanup`, {
            providerId: selectedProvider.id,
          })
        : await api.post('/ai-interviews/spike/live-avatar/cleanup', {
            providerId: selectedProvider.id,
          });
      setCleanupMessage(response.data?.result?.cleanupResult || response.data?.cleanupResult || 'Cleanup request completed.');
      if (providerConversationId && !response.data?.result?.normalizedError) {
        setProviderResult(null);
        setIframeLoaded(false);
      }
    } catch (cleanupError: any) {
      setError(cleanupError.response?.data?.error || cleanupError.message || 'Cleanup request failed.');
    }
  };

  const reconnectProvider = () => {
    setLatencyInput((current) => ({ ...current, reconnectStartedAtMs: performance.now() }));
    setState('RECONNECTING');
    window.setTimeout(() => {
      setLatencyInput((current) => ({ ...current, reconnectedAtMs: performance.now() }));
      setState('CONNECTED');
    }, 150);
  };

  const refreshProviderStatus = async () => {
    const providerConversationId = getProviderConversationId();
    if (!selectedProvider || !providerConversationId) {
      setError('Provider status refresh is available after a provider session has started.');
      return;
    }
    setError(null);
    try {
      const response = await api.get(
        `/ai-interviews/spike/live-avatar/session/${encodeURIComponent(providerConversationId)}/status?providerId=${encodeURIComponent(
          selectedProvider.id
        )}`
      );
      const statusResult = response.data?.result;
      mergeProviderStatusResult(statusResult);
      setProviderStatusRefreshed(true);
      setCleanupMessage('Provider status refreshed. Transcript and recording summaries are redacted.');
    } catch (statusError: any) {
      setError(statusError.response?.data?.error || statusError.message || 'Provider status refresh failed.');
    }
  };

  const stopProviderSession = useCallback(
    async (reason: 'manual' | 'auto_complete' | 'hard_timeout') => {
      const providerConversationId = getProviderConversationId();
      if (!selectedProvider || !providerConversationId) {
        setState('COMPLETED');
        return;
      }

      if (autoCompletionInProgressRef.current) return;
      autoCompletionInProgressRef.current = true;
      setState('CLOSING');
      try {
        if (reason === 'auto_complete') {
          setAutoCompletionMessage(
            'Automatic completion detected: prepared question answered, follow-up limit respected, and closing evidence found. Stopping provider session.'
          );
        }
        if (reason === 'hard_timeout') {
          setAutoCompletionMessage('Hard maximum timeout reached. Stopping the provider session as a safety fallback.');
        }
        const response = await api.post(`/ai-interviews/spike/live-avatar/session/${encodeURIComponent(providerConversationId)}/end`, {
          providerId: selectedProvider.id,
        });
        setProviderSessionStopped(true);
        let message = response.data?.result?.cleanupResult || 'End session request completed.';
        if (selectedProvider.id === 'liveavatar_full') {
          const statusResponse = await api.get(
            `/ai-interviews/spike/live-avatar/session/${encodeURIComponent(providerConversationId)}/status?providerId=${encodeURIComponent(
              selectedProvider.id
            )}`
          );
          mergeProviderStatusResult(statusResponse.data?.result);
          setProviderStatusRefreshed(true);
          message = `${message} Provider status refreshed before cleanup. Transcript and compliance evidence are redacted.`;
          if (reason === 'auto_complete') {
            message = `${message} Automatic interview completion stopped the provider session. Cleanup remains a separate explicit action.`;
          }
          if (reason === 'hard_timeout') {
            message = `${message} Hard timeout stopped the provider session. Cleanup remains a separate explicit action.`;
          }
        }
        setCleanupMessage(message);
      } catch (endError: any) {
        setError(endError.response?.data?.error || endError.message || 'End session request failed.');
        setState('FAILED');
        autoCompletionInProgressRef.current = false;
        return;
      }
      setState('COMPLETED');
      autoCompletionInProgressRef.current = false;
    },
    [getProviderConversationId, mergeProviderStatusResult, selectedProvider]
  );

  const startServerRecording = async () => {
    const providerConversationId = getProviderConversationId();
    if (!providerConversationId || selectedProvider?.id !== 'liveavatar_lite') {
      setError('Server-controlled recording is available after a LiveAvatar LITE room has started.');
      return;
    }
    setError(null);
    setEgressMessage(null);
    setSignedPlaybackUrl(null);
    setSignedPlaybackExpiresAt(null);
    try {
      const response = await api.post(
        `/ai-interviews/spike/live-avatar/session/${encodeURIComponent(providerConversationId)}/egress/start`,
        {
          participantIdentity: 'candidate-fictional-spike',
        }
      );
      const result = response.data?.result as LiveKitEgressResult;
      setEgressResult(result);
      setEgressMessage(
        result.normalizedError
          ? result.normalizedError.message
          : result.duplicatePrevented
            ? 'A server-controlled candidate recording is already active for this room.'
            : 'Server-controlled candidate recording started through LiveKit Egress.'
      );
    } catch (recordingError: any) {
      setError(recordingError.response?.data?.error || recordingError.message || 'Server-controlled recording start failed.');
    }
  };

  const refreshServerRecordingStatus = async () => {
    if (!egressResult?.egressId) {
      setError('Start server-controlled recording before refreshing Egress status.');
      return;
    }
    setError(null);
    try {
      const response = await api.get(`/ai-interviews/spike/live-avatar/egress/${encodeURIComponent(egressResult.egressId)}/status`);
      const result = response.data?.result as LiveKitEgressResult;
      setEgressResult(result);
      setEgressMessage(result.normalizedError ? result.normalizedError.message : `Egress status refreshed: ${result.status}.`);
    } catch (statusError: any) {
      setError(statusError.response?.data?.error || statusError.message || 'Server-controlled recording status refresh failed.');
    }
  };

  const stopServerRecording = async (reason: 'manual' | 'timeout' = 'manual') => {
    if (!egressResult?.egressId) {
      setError('Start server-controlled recording before stopping it.');
      return;
    }
    setError(null);
    try {
      const response = await api.post(`/ai-interviews/spike/live-avatar/egress/${encodeURIComponent(egressResult.egressId)}/stop`, {
        reason,
      });
      const result = response.data?.result as LiveKitEgressResult;
      setEgressResult(result);
      setEgressMessage(
        result.normalizedError
          ? result.normalizedError.message
          : `Server-controlled recording stopped. Stop reason: ${result.stopReason}.`
      );
    } catch (stopError: any) {
      setError(stopError.response?.data?.error || stopError.message || 'Server-controlled recording stop failed.');
    }
  };

  const requestPrivatePlayback = async () => {
    if (!egressResult?.objectKey) {
      setError('A private recording object key is required before playback can be requested.');
      return;
    }
    setError(null);
    try {
      const response = await api.post('/ai-interviews/spike/live-avatar/recordings/signed-url', {
        objectKey: egressResult.objectKey,
      });
      setSignedPlaybackUrl(response.data?.result?.signedUrl || null);
      setSignedPlaybackExpiresAt(response.data?.result?.expiresAt || null);
      setEgressMessage('Private playback URL created for this browser session only.');
    } catch (playbackError: any) {
      setSignedPlaybackUrl(null);
      setSignedPlaybackExpiresAt(null);
      setError(playbackError.response?.data?.error || playbackError.message || 'Private playback URL request failed.');
    }
  };

  const deleteServerRecording = async () => {
    if (!egressResult?.objectKey) {
      setError('A private recording object key is required before deletion.');
      return;
    }
    setError(null);
    try {
      const response = await api.delete('/ai-interviews/spike/live-avatar/recordings', {
        data: { objectKey: egressResult.objectKey },
      });
      setSignedPlaybackUrl(null);
      setSignedPlaybackExpiresAt(null);
      setEgressMessage(
        response.data?.result?.deleted
          ? 'Private recording object deleted from R2.'
          : response.data?.result?.normalizedError?.message || 'Recording deletion completed with a warning.'
      );
    } catch (deleteError: any) {
      setError(deleteError.response?.data?.error || deleteError.message || 'Private recording deletion failed.');
    }
  };

  const endSpike = async () => {
    await stopProviderSession('manual');
  };

  const pollProviderStatusForCompletion = useCallback(async () => {
    const providerConversationId = getProviderConversationId();
    if (!selectedProvider || selectedProvider.id !== 'liveavatar_full' || !providerConversationId || autoCompletionInProgressRef.current) {
      return;
    }

    try {
      const response = await api.get(
        `/ai-interviews/spike/live-avatar/session/${encodeURIComponent(providerConversationId)}/status?providerId=${encodeURIComponent(
          selectedProvider.id
        )}`
      );
      const statusResult = response.data?.result;
      mergeProviderStatusResult(statusResult);
      const completionSignature = buildCompletionEvidenceSignature(statusResult);
      if (!completionSignature) {
        completionCandidateRef.current = null;
        return;
      }

      const now = performance.now();
      if (completionCandidateRef.current?.signature !== completionSignature) {
        completionCandidateRef.current = { signature: completionSignature, firstSeenAtMs: now };
        setAutoCompletionMessage(
          'Potential completion detected. Waiting for the transcript evidence to stay stable before stopping the provider session.'
        );
        return;
      }

      if (now - completionCandidateRef.current.firstSeenAtMs >= LIVEAVATAR_COMPLETION_SETTLE_MS) {
        setProviderStatusRefreshed(true);
        await stopProviderSession('auto_complete');
      }
    } catch {
      // Status polling is best-effort. Manual End Session and the hard timeout remain available.
    }
  }, [getProviderConversationId, mergeProviderStatusResult, selectedProvider, stopProviderSession]);

  useEffect(() => {
    const providerConversationId = getProviderConversationId();
    if (selectedProvider?.id !== 'liveavatar_full' || state !== 'CONNECTED' || !providerConversationId) {
      return;
    }

    void pollProviderStatusForCompletion();
    const pollId = window.setInterval(() => {
      void pollProviderStatusForCompletion();
    }, LIVEAVATAR_STATUS_POLL_MS);
    const timeoutId = window.setTimeout(() => {
      void stopProviderSession('hard_timeout');
    }, LIVEAVATAR_HARD_TIMEOUT_MS);

    return () => {
      window.clearInterval(pollId);
      window.clearTimeout(timeoutId);
    };
  }, [getProviderConversationId, pollProviderStatusForCompletion, selectedProvider?.id, state, stopProviderSession]);

  const resetSpike = () => {
    setError(null);
    setTrackWarning(null);
    setProviderResult(null);
    setCleanupMessage(null);
    setProviderStatusRefreshed(false);
    setProviderSessionStopped(false);
    setEgressResult(null);
    setEgressMessage(null);
    setSignedPlaybackUrl(null);
    setSignedPlaybackExpiresAt(null);
    completionCandidateRef.current = null;
    setAutoCompletionMessage(null);
    setIframeLoaded(false);
    setLatencyInput({});
    setHumanRatings({});
    setHumanNotes('');
    applyEvent('RESET');
  };

  return (
    <EmployerPageShell activeSection="ai-interviews">
      <section className="rounded-[8px] border border-[#f59e0b] bg-[#fffbeb] px-5 py-4 text-[#92400e]">
        <p className="text-[12px] font-bold uppercase tracking-[0.12em]">Internal provider proof of concept</p>
        <p className="mt-1 text-[14px]">
          {config?.plan?.banner || 'Internal provider proof of concept - fictional content only.'}
        </p>
      </section>

      <section className="grid gap-4 lg:grid-cols-[1.3fr_0.95fr]">
        <div className="rounded-[8px] border border-[#dbe7e5] bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <button
                type="button"
                onClick={() => navigate('/employer/ai-interviews')}
                className="text-[12px] font-semibold text-[#0d9488] hover:underline"
              >
                Back to AI Interviews
              </button>
              <h1 className="mt-2 font-display text-[24px] font-bold text-[#0f172a]">Live Avatar Interview Spike</h1>
              <p className="mt-1 max-w-[760px] text-[13px] leading-6 text-[#64748b]">
                Jason-only comparison harness for avatar interview providers. It uses fictional content only and does not
                read or mutate production applications, interviews, assessments, rankings, or simulations.
              </p>
            </div>
            <span className={`rounded-full border px-3 py-1 text-[12px] font-bold ${statusClass[selectedProvider?.readiness.status || 'NOT_CONFIGURED']}`}>
              {selectedProvider?.readiness.status || state}
            </span>
          </div>

          <div className="mt-5 rounded-[8px] border border-[#e2e8f0] bg-[#f8fafc] p-4">
            <label className="text-[12px] font-bold uppercase tracking-[0.12em] text-[#475569]" htmlFor="provider-selector">
              Provider and mode
            </label>
            <select
              id="provider-selector"
              value={selectedProviderId}
              onChange={(event) => {
                setSelectedProviderId(event.target.value);
                setProviderResult(null);
                setCleanupMessage(null);
                setEgressResult(null);
                setEgressMessage(null);
                setSignedPlaybackUrl(null);
                setSignedPlaybackExpiresAt(null);
                setError(null);
              }}
              className="mt-2 w-full rounded-[8px] border border-[#cbd5e1] bg-white px-3 py-2 text-[14px] font-semibold text-[#0f172a]"
            >
              {(config?.providers || []).map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.providerName} - {provider.productMode}
                </option>
              ))}
            </select>
            {selectedProvider && (
              <div className="mt-3 grid gap-2 text-[12px] text-[#475569] md:grid-cols-2">
                <p>
                  <span className="font-semibold text-[#0f172a]">Architecture:</span>{' '}
                  {selectedProvider.architectureCategory.replaceAll('_', ' ')}
                </p>
                <p>
                  <span className="font-semibold text-[#0f172a]">Opt-in flag:</span>{' '}
                  <span className="font-mono">{selectedProvider.optInFlagName}</span>
                </p>
                <p>
                  <span className="font-semibold text-[#0f172a]">LiveKit:</span>{' '}
                  {selectedProvider.liveKitRole.usesLiveKit ? 'Used in this architecture' : 'Not required by default'}
                </p>
                <p>
                  <span className="font-semibold text-[#0f172a]">Pricing:</span>{' '}
                  {selectedProvider.pricing.pricingPubliclyAvailable ? 'Public note available' : 'Not publicly available'}
                </p>
              </div>
            )}
            {selectedProvider?.id === 'tavus' && (
              <div className="mt-3 rounded-[8px] border border-[#bae6fd] bg-[#f0f9ff] p-3 text-[13px] font-semibold text-[#075985]">
                Internal Tavus proof of concept — fictional content only.
              </div>
            )}
            {selectedProvider?.id === 'liveavatar_full' && (
              <div className="mt-3 rounded-[8px] border border-[#bae6fd] bg-[#f0f9ff] p-3 text-[13px] font-semibold text-[#075985]">
                Internal LiveAvatar FULL proof of concept — fictional content only.
              </div>
            )}
            {selectedProvider?.id === 'liveavatar_lite' && (
              <div className="mt-3 rounded-[8px] border border-[#bae6fd] bg-[#f0f9ff] p-3 text-[13px] font-semibold text-[#075985]">
                Internal LiveAvatar LITE proof of concept — Intellerview-owned LiveKit room, fictional content only.
              </div>
            )}
          </div>

          <div className="mt-5 grid gap-4 xl:grid-cols-2">
            <div className="flex min-h-[320px] flex-col rounded-[8px] border border-[#e2e8f0] bg-[#0f172a] p-4 text-white">
              <div className="flex items-center gap-2 text-[13px] font-semibold">
                <Video className="h-4 w-4" />
                Avatar / interviewer video
              </div>
              <div className="mt-4 flex flex-1 items-center justify-center rounded-[8px] border border-dashed border-[#475569] bg-[#111827] text-center">
                {providerResult?.browserSession?.embedStrategy === 'tavus_iframe' ||
                providerResult?.browserSession?.embedStrategy === 'liveavatar_livekit_meet' ? (
                  <iframe
                    title={
                      providerResult.browserSession.embedStrategy === 'liveavatar_livekit_meet'
                        ? selectedProvider?.id === 'liveavatar_lite'
                          ? 'LiveAvatar LITE Intellerview LiveKit room'
                          : 'LiveAvatar FULL LiveKit conversation'
                        : 'Tavus live avatar conversation'
                    }
                    src={providerResult.browserSession.joinUrl}
                    allow="camera *; microphone *; fullscreen *; display-capture *; autoplay *"
                    className="h-full min-h-[280px] w-full rounded-[8px] border-0 bg-black"
                    onLoad={() => {
                      setIframeLoaded(true);
                      setLatencyInput((current) => ({ ...current, firstAvatarRenderAtMs: performance.now() }));
                    }}
                  />
                ) : (
                  <div className="px-4">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#0d9488] text-[24px] font-bold">
                      AI
                    </div>
                    <p className="mt-4 text-[14px] font-semibold">{selectedProvider?.providerName || 'Provider'} avatar area</p>
                    <p className="mt-2 text-[12px] leading-5 text-[#cbd5e1]">
                      The provider room will render here after Jason grants local media and starts a provider test.
                    </p>
                  </div>
                )}
              </div>
              {providerResult?.browserSession && (
                <div className="mt-3 space-y-2 text-[12px] leading-5 text-[#cbd5e1]">
                  <p>
                    Provider room status: {iframeLoaded ? 'Loaded in browser' : 'Waiting for room load'}. Short-lived room access is
                    kept only for this active browser session.
                  </p>
                  {providerResult.browserSession.embedStrategy === 'liveavatar_livekit_meet' && (
                    <a
                      href={providerResult.browserSession.joinUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex rounded-[8px] border border-[#94a3b8] px-3 py-2 text-[12px] font-semibold text-white"
                    >
                      Open LiveKit room in new tab
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className="flex min-h-[320px] flex-col rounded-[8px] border border-[#e2e8f0] bg-white p-4">
              <div className="flex items-center gap-2 text-[13px] font-semibold text-[#0f172a]">
                <Camera className="h-4 w-4" />
                Local camera preview
              </div>
              <div className="mt-4 flex flex-1 items-center justify-center overflow-hidden rounded-[8px] bg-[#e2e8f0]">
                {localStream ? (
                  <video ref={videoRef} autoPlay muted playsInline className="h-full w-full object-cover" />
                ) : (
                  <p className="px-4 text-center text-[13px] text-[#64748b]">Camera preview appears after permission is granted.</p>
                )}
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={requestMedia}
              className="rounded-[8px] border border-[#0d9488] bg-[#0d9488] px-4 py-2 text-[13px] font-semibold text-white"
            >
              Request camera and mic
            </button>
            <button
              type="button"
              disabled={state !== 'READY'}
              onClick={connectProvider}
              className="rounded-[8px] border border-[#0d9488] bg-white px-4 py-2 text-[13px] font-semibold text-[#0d9488] disabled:cursor-not-allowed disabled:border-[#cbd5e1] disabled:text-[#94a3b8]"
            >
              Start Provider Test
            </button>
            <button
              type="button"
              disabled={state !== 'CONNECTED' && state !== 'FAILED'}
              onClick={reconnectProvider}
              className="rounded-[8px] border border-[#cbd5e1] bg-white px-4 py-2 text-[13px] font-semibold text-[#475569] disabled:cursor-not-allowed disabled:text-[#94a3b8]"
            >
              Reconnect test
            </button>
            <button
              type="button"
              disabled={!getProviderConversationId()}
              onClick={refreshProviderStatus}
              className="rounded-[8px] border border-[#cbd5e1] bg-white px-4 py-2 text-[13px] font-semibold text-[#475569] disabled:cursor-not-allowed disabled:text-[#94a3b8]"
            >
              Refresh Provider Status
            </button>
            <button
              type="button"
              onClick={() => void endSpike()}
              className="rounded-[8px] border border-[#64748b] bg-white px-4 py-2 text-[13px] font-semibold text-[#475569]"
            >
              End Session
            </button>
            <button
              type="button"
              disabled={Boolean(
                selectedProvider?.id === 'liveavatar_full' &&
                  getProviderConversationId() &&
                  (!providerSessionStopped || !providerStatusRefreshed)
              )}
              onClick={() => void cleanupProvider()}
              className="inline-flex items-center gap-2 rounded-[8px] border border-[#cbd5e1] bg-white px-4 py-2 text-[13px] font-semibold text-[#475569] disabled:cursor-not-allowed disabled:text-[#94a3b8]"
            >
              <Trash2 className="h-4 w-4" />
              Cleanup Session
            </button>
            <button
              type="button"
              onClick={resetSpike}
              className="inline-flex items-center gap-2 rounded-[8px] border border-[#cbd5e1] bg-white px-4 py-2 text-[13px] font-semibold text-[#475569]"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          </div>

          {error && (
            <div className="mt-4 flex items-start gap-2 rounded-[8px] border border-[#fecaca] bg-[#fef2f2] p-3 text-[13px] text-[#991b1b]">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {trackWarning && (
            <div className="mt-4 flex items-start gap-2 rounded-[8px] border border-[#fde68a] bg-[#fffbeb] p-3 text-[13px] text-[#92400e]">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{trackWarning}</span>
            </div>
          )}
          {cleanupMessage && (
            <div className="mt-4 rounded-[8px] border border-[#dbe7e5] bg-[#f8fafc] p-3 text-[13px] text-[#475569]">
              {cleanupMessage}
            </div>
          )}
          {autoCompletionMessage && (
            <div className="mt-4 rounded-[8px] border border-[#bae6fd] bg-[#f0f9ff] p-3 text-[13px] text-[#075985]">
              {autoCompletionMessage}
            </div>
          )}
          {selectedProvider?.id === 'liveavatar_lite' && getProviderConversationId() && (
            <div className="mt-5 rounded-[8px] border border-[#dbe7e5] bg-white p-4">
              <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                <div>
                  <h2 className="text-[15px] font-bold text-[#0f172a]">Server-controlled recording</h2>
                  <p className="mt-1 max-w-[720px] text-[13px] leading-6 text-[#64748b]">
                    LiveKit Egress records the fictional candidate participant into private Cloudflare R2. Jason requests
                    playback only after recording stops; signed URLs are short-lived and not stored.
                  </p>
                </div>
                <span className="rounded-full border border-[#bae6fd] bg-[#f0f9ff] px-3 py-1 text-[12px] font-bold text-[#075985]">
                  No browser blob
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={Boolean(egressResult?.active)}
                  onClick={() => void startServerRecording()}
                  className="rounded-[8px] border border-[#0d9488] bg-[#0d9488] px-4 py-2 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:border-[#cbd5e1] disabled:bg-[#e2e8f0] disabled:text-[#64748b]"
                >
                  Start server recording
                </button>
                <button
                  type="button"
                  disabled={!egressResult?.egressId}
                  onClick={() => void refreshServerRecordingStatus()}
                  className="rounded-[8px] border border-[#cbd5e1] bg-white px-4 py-2 text-[13px] font-semibold text-[#475569] disabled:cursor-not-allowed disabled:text-[#94a3b8]"
                >
                  Refresh recording status
                </button>
                <button
                  type="button"
                  disabled={!egressResult?.egressId || !egressResult.active}
                  onClick={() => void stopServerRecording('manual')}
                  className="rounded-[8px] border border-[#64748b] bg-white px-4 py-2 text-[13px] font-semibold text-[#475569] disabled:cursor-not-allowed disabled:text-[#94a3b8]"
                >
                  Stop server recording
                </button>
                <button
                  type="button"
                  disabled={!egressResult?.objectKey || egressResult.active}
                  onClick={() => void requestPrivatePlayback()}
                  className="rounded-[8px] border border-[#0d9488] bg-white px-4 py-2 text-[13px] font-semibold text-[#0d9488] disabled:cursor-not-allowed disabled:border-[#cbd5e1] disabled:text-[#94a3b8]"
                >
                  View private playback
                </button>
                <button
                  type="button"
                  disabled={!egressResult?.objectKey}
                  onClick={() => void deleteServerRecording()}
                  className="inline-flex items-center gap-2 rounded-[8px] border border-[#dc2626] bg-white px-4 py-2 text-[13px] font-semibold text-[#991b1b] disabled:cursor-not-allowed disabled:border-[#cbd5e1] disabled:text-[#94a3b8]"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete recording
                </button>
              </div>
              {egressResult && (
                <dl className="mt-4 grid gap-2 text-[12px] md:grid-cols-2">
                  <div className="rounded-[6px] bg-[#f8fafc] px-3 py-2">
                    <dt className="font-semibold text-[#0f172a]">Egress ID</dt>
                    <dd className="mt-1 break-words font-mono text-[#64748b]">{egressResult.egressId || 'Not returned'}</dd>
                  </div>
                  <div className="rounded-[6px] bg-[#f8fafc] px-3 py-2">
                    <dt className="font-semibold text-[#0f172a]">Status</dt>
                    <dd className="mt-1 text-[#64748b]">{egressResult.status}</dd>
                  </div>
                  <div className="rounded-[6px] bg-[#f8fafc] px-3 py-2 md:col-span-2">
                    <dt className="font-semibold text-[#0f172a]">Private object key</dt>
                    <dd className="mt-1 break-words font-mono text-[#64748b]">{egressResult.objectKey}</dd>
                  </div>
                  <div className="rounded-[6px] bg-[#f8fafc] px-3 py-2">
                    <dt className="font-semibold text-[#0f172a]">Duration</dt>
                    <dd className="mt-1 text-[#64748b]">
                      {typeof egressResult.durationMs === 'number' ? `${egressResult.durationMs} ms` : 'Not available yet'}
                    </dd>
                  </div>
                  <div className="rounded-[6px] bg-[#f8fafc] px-3 py-2">
                    <dt className="font-semibold text-[#0f172a]">File size</dt>
                    <dd className="mt-1 text-[#64748b]">
                      {typeof egressResult.fileSizeBytes === 'number' ? `${egressResult.fileSizeBytes} bytes` : 'Not available yet'}
                    </dd>
                  </div>
                </dl>
              )}
              {egressMessage && (
                <div className="mt-4 rounded-[8px] border border-[#dbe7e5] bg-[#f8fafc] p-3 text-[13px] text-[#475569]">
                  {egressMessage}
                </div>
              )}
              {signedPlaybackUrl && (
                <div className="mt-4 rounded-[8px] border border-[#e2e8f0] bg-[#f8fafc] p-3">
                  <p className="text-[12px] font-semibold text-[#0f172a]">
                    Private playback URL expires {signedPlaybackExpiresAt || 'soon'}.
                  </p>
                  <video controls src={signedPlaybackUrl} className="mt-3 aspect-video w-full rounded-[8px] bg-black" />
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="rounded-[8px] border border-[#dbe7e5] bg-white p-5 shadow-sm">
            <h2 className="flex items-center gap-2 text-[15px] font-bold text-[#0f172a]">
              <Radio className="h-4 w-4 text-[#0d9488]" />
              State and readiness
            </h2>
            <p className="mt-3 text-[13px] leading-6 text-[#64748b]">{stateDescriptions[state]}</p>
            <div className="mt-4 rounded-[8px] bg-[#f8fafc] p-3 text-[12px] leading-5 text-[#475569]">
              <p className="font-semibold text-[#0f172a]">Provider readiness</p>
              <p>{selectedProvider?.readiness.blockedReason || 'Loading provider status...'}</p>
              {selectedProvider && selectedProvider.readiness.missingCredentialNames.length > 0 && (
                <p className="mt-2">
                  Missing variable names:{' '}
                  <span className="font-mono">{selectedProvider.readiness.missingCredentialNames.join(', ')}</span>
                </p>
              )}
              {selectedProvider && selectedProvider.readiness.configuredCredentialNames.length > 0 && (
                <p className="mt-2">
                  Present variable names:{' '}
                  <span className="font-mono">{selectedProvider.readiness.configuredCredentialNames.join(', ')}</span>
                </p>
              )}
            </div>
          </div>

          <div className="rounded-[8px] border border-[#dbe7e5] bg-white p-5 shadow-sm">
            <h2 className="flex items-center gap-2 text-[15px] font-bold text-[#0f172a]">
              <ShieldCheck className="h-4 w-4 text-[#0d9488]" />
              Camera and microphone
            </h2>
            <dl className="mt-3 grid grid-cols-2 gap-2 text-[12px]">
              <dt className="text-[#64748b]">Camera permission</dt>
              <dd className="font-semibold text-[#0f172a]">{cameraReady ? 'Granted' : 'Not granted'}</dd>
              <dt className="text-[#64748b]">Microphone permission</dt>
              <dd className="font-semibold text-[#0f172a]">{microphoneReady ? 'Granted' : 'Not granted'}</dd>
              <dt className="text-[#64748b]">Video track</dt>
              <dd className="font-semibold text-[#0f172a]">{videoTrackActive ? 'Active' : 'Inactive'}</dd>
              <dt className="text-[#64748b]">Audio track</dt>
              <dd className="font-semibold text-[#0f172a]">{audioTrackActive ? 'Active' : 'Inactive'}</dd>
            </dl>
            <div className="mt-4 rounded-[8px] bg-[#f8fafc] p-3 text-[12px] leading-5 text-[#475569]">
              <p className="font-semibold text-[#0f172a]">Official recording path</p>
              <p className="mt-1">
                Official interview recording must be centrally controlled by Intellerview or the selected provider. Browser
                recording blobs are not accepted as production evidence.
              </p>
            </div>
          </div>

          {selectedProvider && (
            <div className="rounded-[8px] border border-[#dbe7e5] bg-white p-5 shadow-sm">
              <h2 className="flex items-center gap-2 text-[15px] font-bold text-[#0f172a]">
                <Download className="h-4 w-4 text-[#0d9488]" />
                Central recording requirement
              </h2>
              <p className="mt-3 text-[13px] leading-6 text-[#64748b]">
                A provider is blocked from production selection until this path is proven with server/provider controlled
                recording, retrieval, timestamp alignment and deletion.
              </p>
              <dl className="mt-4 grid grid-cols-2 gap-2 text-[12px]">
                <dt className="text-[#64748b]">Room owner</dt>
                <dd className="font-semibold text-[#0f172a]">{selectedProvider.recordingArchitecture.roomOwner}</dd>
                <dt className="text-[#64748b]">Recording controller</dt>
                <dd className="font-semibold text-[#0f172a]">{selectedProvider.recordingArchitecture.recordingController}</dd>
                <dt className="text-[#64748b]">Candidate track</dt>
                <dd className="font-semibold text-[#0f172a]">
                  {formatCapabilityValue(selectedProvider.recordingArchitecture.candidateTrackRecordingAvailable)}
                </dd>
                <dt className="text-[#64748b]">Avatar track</dt>
                <dd className="font-semibold text-[#0f172a]">
                  {formatCapabilityValue(selectedProvider.recordingArchitecture.avatarTrackRecordingAvailable)}
                </dd>
                <dt className="text-[#64748b]">Combined room</dt>
                <dd className="font-semibold text-[#0f172a]">
                  {formatCapabilityValue(selectedProvider.recordingArchitecture.combinedRoomRecordingAvailable)}
                </dd>
                <dt className="text-[#64748b]">Separate export</dt>
                <dd className="font-semibold text-[#0f172a]">
                  {formatCapabilityValue(selectedProvider.recordingArchitecture.separateTrackExportAvailable)}
                </dd>
                <dt className="text-[#64748b]">Can Intellerview stop?</dt>
                <dd className="font-semibold text-[#0f172a]">
                  {formatCapabilityValue(selectedProvider.recordingArchitecture.intellerviewCanInitiateAndStopRecording)}
                </dd>
                <dt className="text-[#64748b]">Refresh survival</dt>
                <dd className="font-semibold text-[#0f172a]">
                  {formatCapabilityValue(selectedProvider.recordingArchitecture.continuesIfCandidateRefreshes)}
                </dd>
                <dt className="text-[#64748b]">Transcript alignment</dt>
                <dd className="font-semibold text-[#0f172a]">
                  {formatCapabilityValue(selectedProvider.recordingArchitecture.transcriptToRecordingTimestampAlignment)}
                </dd>
                <dt className="text-[#64748b]">Production suitability</dt>
                <dd className="font-semibold text-[#0f172a]">
                  {formatCapabilityValue(selectedProvider.recordingArchitecture.productionSuitability)}
                </dd>
              </dl>
              <div className="mt-3 space-y-2 rounded-[8px] bg-[#f8fafc] p-3 text-[12px] leading-5 text-[#475569]">
                <p>
                  <span className="font-semibold text-[#0f172a]">Asset ownership:</span>{' '}
                  {selectedProvider.recordingArchitecture.recordingAssetOwnership}
                </p>
                <p>
                  <span className="font-semibold text-[#0f172a]">Retrieval:</span>{' '}
                  {selectedProvider.recordingArchitecture.recordingRetrievalMechanism}
                </p>
                <p>
                  <span className="font-semibold text-[#0f172a]">Deletion:</span>{' '}
                  {selectedProvider.recordingArchitecture.deletionMechanism}
                </p>
                <p>{selectedProvider.recordingArchitecture.note}</p>
              </div>
            </div>
          )}

          <div className="rounded-[8px] border border-[#dbe7e5] bg-white p-5 shadow-sm">
            <h2 className="flex items-center gap-2 text-[15px] font-bold text-[#0f172a]">
              <Timer className="h-4 w-4 text-[#0d9488]" />
              Latency measurements
            </h2>
            <dl className="mt-3 space-y-2 text-[12px]">
              {Object.entries(latencySummary).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between gap-3">
                  <dt className="text-[#64748b]">{key}</dt>
                  <dd className="font-semibold text-[#0f172a]">{formatLatency(value)}</dd>
                </div>
              ))}
            </dl>
          </div>
        </aside>
      </section>

      {selectedProvider && (
        <section className="grid gap-4 xl:grid-cols-[1fr_1fr]">
          <div className="rounded-[8px] border border-[#dbe7e5] bg-white p-5 shadow-sm">
            <h2 className="text-[15px] font-bold text-[#0f172a]">Architecture ownership</h2>
            <dl className="mt-3 grid gap-2 text-[12px] md:grid-cols-2">
              {Object.entries(selectedProvider.liveKitRole).map(([key, value]) => (
                <div key={key} className="rounded-[6px] bg-[#f8fafc] px-3 py-2">
                  <dt className="font-semibold text-[#0f172a]">{key}</dt>
                  <dd className="mt-1 leading-5 text-[#64748b]">{String(value)}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="rounded-[8px] border border-[#dbe7e5] bg-white p-5 shadow-sm">
            <h2 className="text-[15px] font-bold text-[#0f172a]">Capabilities to verify</h2>
            <div className="mt-3 space-y-2">
              {selectedProvider.capabilities.map((capability) => (
                <div key={`${capability.label}-${capability.status}`} className="rounded-[6px] border border-[#e2e8f0] p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-[13px] font-semibold text-[#0f172a]">{capability.label}</p>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${sourceClass[capability.status]}`}>
                      {capability.status.replaceAll('_', ' ')}
                    </span>
                  </div>
                  <p className="mt-1 text-[12px] leading-5 text-[#64748b]">{capability.note}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-[8px] border border-[#dbe7e5] bg-white p-5 shadow-sm">
          <h2 className="text-[15px] font-bold text-[#0f172a]">Standard fictional interview plan</h2>
          <div className="mt-3 space-y-3 text-[13px] leading-6 text-[#64748b]">
            <p>
              <span className="font-semibold text-[#0f172a]">Core question:</span> {config?.plan?.coreQuestion}
            </p>
            <p>
              <span className="font-semibold text-[#0f172a]">Allowed follow-up:</span>{' '}
              {config?.plan?.allowedFollowUpPurpose}
            </p>
            <p>
              <span className="font-semibold text-[#0f172a]">Controlled follow-up text:</span>{' '}
              {config?.plan?.controlledFollowUp}
            </p>
            <p>
              <span className="font-semibold text-[#0f172a]">Stop condition:</span> {config?.plan?.stopCondition}
            </p>
          </div>
          <ol className="mt-4 space-y-2 text-[12px] leading-5 text-[#64748b]">
            {(config?.standardTestScript || []).map((step, index) => (
              <li key={step}>
                <span className="font-semibold text-[#0f172a]">{index + 1}.</span> {step}
              </li>
            ))}
          </ol>
        </div>

        <div className="rounded-[8px] border border-[#dbe7e5] bg-white p-5 shadow-sm">
          <h2 className="text-[15px] font-bold text-[#0f172a]">Transcript preview</h2>
          <p className="mt-2 text-[13px] leading-6 text-[#64748b]">
            Transcript content is displayed on screen only. The spike does not print transcript text to server logs.
          </p>
          <div className="mt-4 rounded-[8px] border border-[#e2e8f0] bg-[#f8fafc] p-3">
            {transcriptSegments.length === 0 ? (
              <p className="text-[13px] text-[#64748b]">No transcript yet. A real provider run is required.</p>
            ) : (
              transcriptSegments.map((segment, index) => (
                <div key={`${segment.speaker}-${index}`} className="text-[13px]">
                  <span className="font-semibold">{segment.speaker}:</span> {segment.text}
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {selectedProvider && (
        <section className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-[8px] border border-[#dbe7e5] bg-white p-5 shadow-sm">
            <h2 className="flex items-center gap-2 text-[15px] font-bold text-[#0f172a]">
              <Download className="h-4 w-4 text-[#0d9488]" />
              Pricing metadata
            </h2>
            <p className="mt-2 text-[13px] leading-6 text-[#64748b]">
              Detailed commercial comparison is intentionally handled outside this application.
            </p>
            <dl className="mt-4 grid gap-2 text-[12px]">
              <div className="rounded-[6px] bg-[#f8fafc] px-3 py-2">
                <dt className="font-semibold text-[#0f172a]">Public charging unit</dt>
                <dd className="mt-1 leading-5 text-[#64748b]">{selectedProvider.pricing.publicChargingUnit}</dd>
              </div>
              <div className="rounded-[6px] bg-[#f8fafc] px-3 py-2">
                <dt className="font-semibold text-[#0f172a]">Public plan name</dt>
                <dd className="mt-1 leading-5 text-[#64748b]">{selectedProvider.pricing.publicPlanName}</dd>
              </div>
              <div className="rounded-[6px] bg-[#f8fafc] px-3 py-2">
                <dt className="font-semibold text-[#0f172a]">Public session-duration limit</dt>
                <dd className="mt-1 leading-5 text-[#64748b]">{selectedProvider.pricing.publicSessionDurationLimit}</dd>
              </div>
              <div className="rounded-[6px] bg-[#f8fafc] px-3 py-2">
                <dt className="font-semibold text-[#0f172a]">Public concurrency limit</dt>
                <dd className="mt-1 leading-5 text-[#64748b]">{selectedProvider.pricing.publicConcurrencyLimit}</dd>
              </div>
              <div className="rounded-[6px] bg-[#f8fafc] px-3 py-2">
                <dt className="font-semibold text-[#0f172a]">Pricing note</dt>
                <dd className="mt-1 leading-5 text-[#64748b]">{selectedProvider.pricing.pricingNote}</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-[8px] border border-[#dbe7e5] bg-white p-5 shadow-sm">
            <h2 className="flex items-center gap-2 text-[15px] font-bold text-[#0f172a]">
              <SlidersHorizontal className="h-4 w-4 text-[#0d9488]" />
              Jason human rating
            </h2>
            <p className="mt-2 text-[13px] leading-6 text-[#64748b]">
              Local notes for provider selection only. These are not candidate assessment metrics and are not saved.
            </p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {ratingFields.map((field) => (
                <label key={field} className="text-[12px] font-semibold text-[#475569]">
                  {field}
                  <select
                    value={humanRatings[field] || ''}
                    onChange={(event) => setHumanRatings((current) => ({ ...current, [field]: event.target.value }))}
                    className="mt-1 w-full rounded-[6px] border border-[#cbd5e1] bg-white px-2 py-1.5 text-[#0f172a]"
                  >
                    <option value="">Not rated</option>
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <option key={rating} value={rating}>
                        {rating}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <label className="mt-4 block text-[12px] font-semibold text-[#475569]">
              Notes
              <textarea
                value={humanNotes}
                onChange={(event) => setHumanNotes(event.target.value)}
                rows={4}
                className="mt-1 w-full rounded-[6px] border border-[#cbd5e1] px-3 py-2 text-[13px] text-[#0f172a]"
                placeholder="Uncanny behavior, distracting movement, robotic pauses, interruption handling, visible glitches, candidate comfort."
              />
            </label>
          </div>
        </section>
      )}

      {providerResult && (
        <section className="rounded-[8px] border border-[#dbe7e5] bg-white p-5 shadow-sm">
          <h2 className="text-[15px] font-bold text-[#0f172a]">Structured comparison result</h2>
          <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {[
              ['Status', providerResult.status],
              ['Avatar render', providerResult.avatarRenderResult],
              ['Candidate camera', providerResult.candidateCameraResult],
              ['Exact question', providerResult.exactQuestionResult],
              ['Follow-up', providerResult.followUpResult],
              ['Closing', providerResult.closingResult],
              ['Transcript', providerResult.transcriptResult],
              ['Timestamps', providerResult.timestampResult],
              ['Recording/export', providerResult.recordingResult],
              ['Candidate-only track', providerResult.candidateTrackResult],
              ['Reconnect', providerResult.reconnectResult],
              ['Cleanup', providerResult.cleanupResult],
              ['Short-lived credentials', providerResult.shortLivedCredentialReturned ? 'Returned but redacted' : 'Not returned'],
              [
                'Tavus conversation',
                providerResult.providerConversationId
                  ? `Active browser session for ${providerResult.providerName}; identifier is held only in this page state.`
                  : 'No provider conversation ID returned.',
              ],
              [
                'Transcript status',
                providerResult.transcriptStatus
                  ? `${providerResult.transcriptStatus.available ? 'Available' : 'Not available'}; segments: ${
                      providerResult.transcriptStatus.segmentCount
                    }; raw transcript not returned.`
                  : 'Not checked.',
              ],
              [
                'Question evidence',
                providerResult.transcriptEvidence
                  ? `Source: ${providerResult.transcriptEvidence.source}; core detected: ${
                      providerResult.transcriptEvidence.coreQuestionDetected ? 'yes' : 'no'
                    }; core count: ${providerResult.transcriptEvidence.coreQuestionCount}; follow-up count: ${
                      providerResult.transcriptEvidence.followUpCount
                    }; max one follow-up: ${providerResult.transcriptEvidence.maxOneFollowUp ? 'yes' : 'no'}.`
                  : 'Not checked.',
              ],
              [
                'Closing evidence',
                providerResult.transcriptEvidence
                  ? `Closing detected: ${providerResult.transcriptEvidence.closingDetected ? 'yes' : 'no'}; assistant turns: ${
                      providerResult.transcriptEvidence.assistantTurnCount
                    }; candidate turns: ${providerResult.transcriptEvidence.candidateTurnCount}.`
                  : 'Not checked.',
              ],
              [
                'Transcript metadata',
                providerResult.transcriptEvidence
                  ? `Speaker labels: ${
                      providerResult.transcriptEvidence.transcriptSpeakerLabelsAvailable ? 'available' : 'not available'
                    }; timestamps: ${
                      providerResult.transcriptEvidence.transcriptTimestampAvailable ? 'available' : 'not available'
                    }; raw transcript not returned.`
                  : 'Not checked.',
              ],
              [
                'LITE worker events',
                providerResult.workerEventSummary
                  ? `Events: ${providerResult.workerEventSummary.eventCount}; completion: ${
                      providerResult.workerEventSummary.completionEventSeen ? 'seen' : 'not seen'
                    }; failed: ${providerResult.workerEventSummary.failedEventSeen ? 'yes' : 'no'}; raw transcript not returned.`
                  : 'Not checked.',
              ],
              [
                'LITE worker states',
                providerResult.workerEvents && providerResult.workerEvents.length > 0
                  ? providerResult.workerEvents
                      .slice(-6)
                      .map((event) => `${event.type}${event.speaker ? ` (${event.speaker})` : ''}`)
                      .join(', ')
                  : 'No worker events captured yet.',
              ],
              [
                'Recording status',
                providerResult.recordingStatus
                  ? `${providerResult.recordingStatus.available ? 'Available' : 'Not available'}; ${
                      providerResult.recordingStatus.note
                    }`
                  : 'Not checked.',
              ],
              [
                'Candidate video',
                providerResult.recordingStatus
                  ? providerResult.recordingStatus.candidateOnlyAvailable
                    ? 'Candidate-only recording available.'
                    : 'Candidate-only video not available from iframe mode; use Daily SDK track access or a separate recording layer.'
                  : 'Not checked.',
              ],
              [
                'Provider events',
                providerResult.providerEventNames && providerResult.providerEventNames.length > 0
                  ? providerResult.providerEventNames.join(', ')
                  : 'No provider events captured yet.',
              ],
            ].map(([label, value]) => (
              <div key={label} className="rounded-[6px] bg-[#f8fafc] px-3 py-2">
                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#94a3b8]">{label}</p>
                <p className="mt-1 text-[12px] leading-5 text-[#475569]">{value}</p>
              </div>
            ))}
          </div>
          {((providerResult.blockers?.length || 0) > 0 || (providerResult.warnings?.length || 0) > 0) && (
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div className="rounded-[6px] border border-[#fde68a] bg-[#fffbeb] p-3">
                <p className="text-[12px] font-bold text-[#92400e]">Blockers</p>
                <ul className="mt-2 space-y-1 text-[12px] leading-5 text-[#92400e]">
                  {(providerResult.blockers || []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-[6px] border border-[#e2e8f0] bg-[#f8fafc] p-3">
                <p className="text-[12px] font-bold text-[#475569]">Warnings</p>
                <ul className="mt-2 space-y-1 text-[12px] leading-5 text-[#64748b]">
                  {(providerResult.warnings || []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </section>
      )}

      {selectedProvider && (
        <section className="rounded-[8px] border border-[#dbe7e5] bg-white p-5 shadow-sm">
          <h2 className="text-[15px] font-bold text-[#0f172a]">Setup steps for Jason</h2>
          <ol className="mt-3 space-y-2 text-[13px] leading-6 text-[#64748b]">
            {selectedProvider.setupSteps.map((step, index) => (
              <li key={step}>
                <span className="font-semibold text-[#0f172a]">{index + 1}.</span> {step}
              </li>
            ))}
          </ol>
        </section>
      )}
    </EmployerPageShell>
  );
};

export default LiveAvatarSpike;
